import { useMemo, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Header.module.css';

import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { useSlice } from '../../../hooks/useSlice';
import { selectMenu } from '../../../features/infoSlice';
import { useGetSortByQuery } from '../../../services/common';
import { useInitDriveQuery } from '../../../services/drive';

import Dropdown from '../../Dropdown';
import OrderArrow from '../../OrderArrow';

import {
  VIEW,
  SELECT,
  DOWNLOAD,
  RESTORE,
  TITLE,
  FILE,
  CATEGORY,
  HITS,
  OWNER,
  FILE_SIZE,
} from '../../../constants';
import { Options } from '../../../types/library';
import { SortDirection, SortType } from '../../../types/common';

export default function Header() {
  const dispatch = useAppDispatch();
  const cx = classNames.bind(styles);
  const menu = useAppSelector(selectMenu);
  const tag =
    menu === 'library' ? 'personal' : menu === 'mydrive' ? 'archive' : menu;
  const { data, isLoading } =
    tag === 'archive' ? useInitDriveQuery() : useGetSortByQuery(tag);
  const sortBy = data?.sortByMap || data;
  const { orderChanged, selectOptions, sortTypeChanged } = useSlice();

  const { isEditMode, sortType, sortOrder }: Options =
    useAppSelector(selectOptions);
  const [sortTypeByDate, setSortTypeByDate] = useState(sortType);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const orderRef = useRef(null);

  const sortDisabled = useMemo(
    () => (isEditMode ? 'disabled' : ''),
    [isEditMode]
  );

  const dateTypesByMenu: { [key: string]: string[] } = {
    library: ['reg_date', 'last_date'],
    share: ['share_date', 'last_date'],
    mydrive: ['reg_date'],
    search: ['reg_date'],
    trash: ['delete_date', 'upload_date'],
  };
  const dateTypes = dateTypesByMenu[menu];
  const hasSortDropdown = dateTypes.length > 1;

  const toggleDropdownMenu = () => {
    if (isEditMode) return;
    setIsDropdownOpen(!isDropdownOpen);
  };

  const sortByDate = (name: SortType) => {
    dispatch(orderChanged(SortDirection.DESC));
    dispatch(sortTypeChanged(name));
    setIsDropdownOpen(false);
    setSortTypeByDate(name);
  };

  const changeSortType = (type: SortType) => {
    if (isEditMode) return;
    dispatch(orderChanged(SortDirection.ASC));
    dispatch(sortTypeChanged(type));
  };

  const changeOrder = () => {
    if (isEditMode) return;
    const order =
      sortOrder === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
    dispatch(orderChanged(order));
  };

  if (isLoading) return null;

  return (
    <ul className={cx('book_list_head', menu)}>
      <li>
        <span className={cx('option_order', { btn: !isEditMode })}>
          <span
            className={cx('is_order')}
            onClick={() => {
              changeSortType(SortType.EXT_TYPE);
              changeOrder();
            }}
          >
            <span className={cx('open_order_menu')}>{CATEGORY}</span>
            {!isEditMode && sortType === SortType.EXT_TYPE && (
              <OrderArrow classnames={[sortOrder, sortDisabled]} />
            )}
          </span>
        </span>
      </li>
      <li>
        <span className={cx('option_order', { btn: !isEditMode })}>
          <span
            className={cx('is_order')}
            onClick={() => {
              changeSortType(
                menu === 'mydrive' || menu === 'trash'
                  ? SortType.ORG_FILE_NAME
                  : SortType.TITLE
              );
              changeOrder();
            }}
          >
            <span className={cx('open_order_menu')}>
              {menu === 'mydrive' || menu === 'trash' ? FILE : TITLE}
            </span>
            {!isEditMode &&
              (sortType === 'title' || sortType === 'org_file_name') && (
                <OrderArrow classnames={[sortOrder, sortDisabled]} />
              )}
          </span>
        </span>
      </li>
      {(menu === 'mydrive' || menu === 'trash') && (
        <li>
          <span className={cx('option_order', { btn: !isEditMode })}>
            <span
              className={cx('is_order')}
              onClick={() => {
                changeSortType(SortType.SIZE);
                changeOrder();
              }}
            >
              <span className={cx('open_order_menu')}>{FILE_SIZE}</span>
              {!isEditMode && sortType === SortType.SIZE && (
                <OrderArrow classnames={[sortOrder, sortDisabled]} />
              )}
            </span>
          </span>
        </li>
      )}
      {menu === 'share' && (
        <>
          <li>
            <span className={cx('option_order')}>
              <span className={cx('is_order')}>
                <span className={cx('open_order_menu')}>{OWNER}</span>
              </span>
            </span>
          </li>
        </>
      )}
      {menu === 'search' && (
        <>
          <li>
            <span className={cx('option_order', { btn: !isEditMode })}>
              <span
                className={cx('is_order')}
                onClick={() => {
                  changeSortType(SortType.HIT);
                  changeOrder();
                }}
              >
                <span className={cx('open_order_menu')}>{HITS}</span>
                {!isEditMode && sortType === SortType.HIT && (
                  <OrderArrow classnames={[sortOrder, sortDisabled]} />
                )}
              </span>
            </span>
          </li>
          <li>
            <span className={cx('option_order')}>
              <span className={cx('is_order')}>
                <span className={cx('open_order_menu')}>{OWNER}</span>
              </span>
            </span>
          </li>
        </>
      )}
      <li>
        <span
          className={cx(
            'option_order',
            { btn: !isEditMode },
            { dropdown: hasSortDropdown }
          )}
        >
          <span
            className={cx('is_order')}
            onClick={() => {
              if (!hasSortDropdown) {
                changeSortType(SortType.REG_DATE);
                changeOrder();
              }
            }}
          >
            <span
              className={cx('open_order_menu')}
              onClick={() =>
                hasSortDropdown
                  ? toggleDropdownMenu()
                  : sortByDate(SortType.REG_DATE)
              }
              ref={orderRef}
            >
              {sortTypeByDate.includes('date')
                ? sortBy[sortTypeByDate]
                : Object.values(sortBy)[0]}
            </span>
            {!isEditMode &&
              (sortType === 'reg_date' ||
                sortType === 'last_date' ||
                sortType === 'share_date' ||
                sortType === 'delete_date' ||
                sortType === 'upload_date') && (
                <OrderArrow
                  clickHandler={changeOrder}
                  classnames={[sortOrder, sortDisabled]}
                />
              )}
          </span>
        </span>
        {hasSortDropdown && (
          <Dropdown
            name="sortType"
            classnames={['type_order_menu', 'list']}
            type={sortType}
            list={dateTypes}
            clickHandlers={[sortByDate]}
            isOpen={isDropdownOpen}
            button={orderRef}
            backdropHandler={() => setIsDropdownOpen(false)}
            data={sortBy}
          />
        )}
      </li>
      <li>
        <span>
          {!isEditMode
            ? menu === 'mydrive'
              ? DOWNLOAD
              : menu === 'trash'
              ? RESTORE
              : VIEW
            : SELECT}
        </span>
      </li>
    </ul>
  );
}
